// components/ImageUploadField.jsx
import React, { useRef, useState } from 'react';
import {
    Box,
    Button,
    Avatar,
    Typography,
    CircularProgress,
    alpha,
    useTheme,
    Stack,
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Image as ImageIcon,
    CameraAlt as CameraAltIcon,
} from '@mui/icons-material';
import CameraCapture from './CameraCapture'; // Import the CameraCapture component

const ImageUploadField = ({
    value, // existing image URL
    onChange, // callback when image is uploaded
    onRemove, // callback when image is removed
    label = "Product Image",
    cloudName = "df9o4e6xl", // Replace with your cloud name
    uploadPreset = "sanyam",
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const widgetRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(value || null);
    const [cameraOpen, setCameraOpen] = useState(false);

    const uploadToCloudinary = async (file) => {
        setUploading(true);
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', uploadPreset);
        formData.append('cloud_name', cloudName);
        formData.append('folder', 'jewelry-items');

        try {
            const response = await fetch(
                `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
                {
                    method: 'POST',
                    body: formData,
                }
            );
            const data = await response.json();
            
            if (data.secure_url) {
                const imageUrl = data.secure_url;
                const publicId = data.public_id;
                console.log('Upload successful:', imageUrl);
                setPreviewUrl(imageUrl);
                setUploading(false);
                onChange(imageUrl, publicId);
                return true;
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            setUploading(false);
            alert('Failed to upload image. Please try again.');
            return false;
        }
    };

    const openWidget = () => {
        if (widgetRef.current) {
            widgetRef.current.open();
            return;
        }

        if (window.cloudinary) {
            widgetRef.current = window.cloudinary.createUploadWidget(
                {
                    cloudName: cloudName,
                    uploadPreset: uploadPreset,
                    sources: ['local', 'google_drive', 'dropbox'],
                    multiple: false,
                    cropping: true,
                    croppingAspectRatio: 1,
                    croppingCoordinatesMode: 'custom',
                    showAdvancedOptions: false,
                    showPoweredBy: false,
                    folder: 'jewelry-items',
                    clientAllowedFormats: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
                    maxFileSize: 5000000, // 5MB
                    styles: {
                        palette: {
                            window: isDark ? '#1e293b' : '#ffffff',
                            windowBorder: isDark ? '#334155' : '#e2e8f0',
                            tabIcon: isDark ? '#94a3b8' : '#64748b',
                            menuIcons: isDark ? '#94a3b8' : '#64748b',
                            textDark: isDark ? '#f1f5f9' : '#1e293b',
                            textLight: '#ffffff',
                            link: '#3b82f6',
                            action: '#10b981',
                            inactiveTabIcon: isDark ? '#475569' : '#cbd5e1',
                            error: '#ef4444',
                            inProgress: '#8b5cf6',
                            complete: '#10b981',
                            sourceBg: isDark ? '#0f172a' : '#f8fafc',
                        },
                    },
                },
                (error, result) => {
                    if (error) {
                        console.error('Upload error:', error);
                        setUploading(false);
                        alert('Failed to upload image. Please try again.');
                        return;
                    }

                    if (result && result.event === 'success') {
                        const imageUrl = result.info.secure_url;
                        const publicId = result.info.public_id;
                        console.log('Upload successful:', imageUrl);
                        setPreviewUrl(imageUrl);
                        setUploading(false);
                        onChange(imageUrl, publicId);
                    }

                    if (result && result.event === 'close') {
                        setUploading(false);
                    }
                }
            );
            setUploading(true);
            widgetRef.current.open();
        } else {
            console.error('Cloudinary widget script not loaded');
            alert('Upload service not ready. Please refresh the page.');
        }
    };

    const handleCameraCapture = async (file) => {
        await uploadToCloudinary(file);
        setCameraOpen(false);
    };

    const handleRemoveImage = () => {
        setPreviewUrl(null);
        if (onRemove) {
            onRemove();
        }
    };

    const getOptimizedImageUrl = (url) => {
        if (!url) return url;
        // Insert optimization parameters before the upload version
        return url.replace('/upload/', '/upload/q_auto,f_auto/');
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Typography
                variant="subtitle2"
                sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: isDark ? '#e2e8f0' : '#475569',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <ImageIcon fontSize="small" />
                {label}
            </Typography>

            {previewUrl ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        background: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                        ),
                        border: `1px solid ${alpha(
                            isDark ? theme.palette.divider : '#e2e8f0',
                            isDark ? 0.2 : 0.3
                        )}`,
                    }}
                >
                    <Avatar
                        src={getOptimizedImageUrl(previewUrl)}
                        variant="rounded"
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 2,
                            border: `2px solid ${alpha('#10b981', 0.3)}`,
                        }}
                    />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                            Image uploaded successfully
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={openWidget}
                                disabled={uploading}
                                sx={{ borderRadius: 2 }}
                            >
                                Change
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setCameraOpen(true)}
                                disabled={uploading}
                                startIcon={<CameraAltIcon />}
                                sx={{ borderRadius: 2 }}
                            >
                                Take New Photo
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={handleRemoveImage}
                                startIcon={<DeleteIcon />}
                                sx={{ borderRadius: 2 }}
                            >
                                Remove
                            </Button>
                        </Box>
                    </Box>
                </Box>
            ) : (
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                    <Button
                        variant="outlined"
                        startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                        onClick={openWidget}
                        disabled={uploading}
                        sx={{
                            flex: 1,
                            borderRadius: 2,
                            py: 3,
                            backdropFilter: 'blur(10px)',
                            background: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.3 : 0.8
                            ),
                            border: `2px dashed ${alpha(
                                isDark ? theme.palette.divider : '#10b981',
                                isDark ? 0.2 : 0.5
                            )}`,
                            '&:hover': {
                                border: `2px dashed ${alpha('#10b981', 0.8)}`,
                                background: alpha('#10b981', 0.05),
                            },
                        }}
                    >
                        <Typography variant="body2">
                            {uploading ? 'Uploading...' : `Upload ${label.toLowerCase()}`}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: isDark ? '#94a3b8' : '#64748b' }}>
                            Max 5MB. Supports JPG, PNG, GIF
                        </Typography>
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={() => setCameraOpen(true)}
                        disabled={uploading}
                        startIcon={<CameraAltIcon />}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            backdropFilter: 'blur(10px)',
                            background: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.3 : 0.8
                            ),
                            border: `2px solid ${alpha(
                                isDark ? theme.palette.divider : '#8b5cf6',
                                isDark ? 0.2 : 0.5
                            )}`,
                            '&:hover': {
                                border: `2px solid ${alpha('#8b5cf6', 0.8)}`,
                                background: alpha('#8b5cf6', 0.05),
                            },
                        }}
                    >
                        <Stack direction="column" alignItems="center">
                            <Typography variant="caption">Take Photo</Typography>
                        </Stack>
                    </Button>
                </Stack>
            )}

            {/* Camera Dialog */}
            <CameraCapture
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCapture={handleCameraCapture}
                isDark={isDark}
            />
        </Box>
    );
};

export default ImageUploadField;